/**
 * 45R Mobile Memory Manager
 *
 * Prevents iOS Safari memory crashes on collection pages with infinite scroll.
 * When users scroll through many pages (e.g., 10+ pages of 24 products each),
 * the accumulated decoded image bitmaps can exceed mobile Safari's ~1.5GB memory
 * limit, causing the "A problem repeatedly occurred" crash.
 *
 * Solution: On mobile devices only (<750px), this script unloads images from
 * pages that are far from the current viewport by swapping their src/srcset
 * to a tiny transparent GIF. When the user scrolls back, the original src/srcset
 * is restored from data attributes.
 *
 * This script is fully standalone and does NOT modify any existing code.
 * It only manipulates img src/srcset attributes on product grid cards.
 * To disable: remove the <script> tag from 45R-main-collection.liquid.
 */
(function () {
  'use strict';

  // --- Configuration ---
  var PAGE_BUFFER = 1; // Keep images loaded for ±1 page around current view
  var SCROLL_THROTTLE_MS = 150; // Minimum ms between scroll-triggered checks
  var BLANK_SRC =
    'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

  // --- State ---
  var grid = null;
  var unloadedPages = {}; // { [pageNumber]: true }
  var throttleTimer = null;
  var isActive = false;

  // --- Guards ---

  /** Check if we should activate (mobile viewport only) */
  function shouldActivate() {
    return window.innerWidth < 750;
  }

  // --- Core Logic ---

  /**
   * Get all page numbers present in the grid and their card elements.
   * Returns { [pageNumber]: HTMLElement[] }
   */
  function getPageGroups() {
    var groups = {};
    var cards = grid.querySelectorAll('[data-page]');
    for (var i = 0; i < cards.length; i++) {
      var pageNum = parseInt(cards[i].getAttribute('data-page'), 10);
      if (isNaN(pageNum)) continue;
      if (!groups[pageNum]) groups[pageNum] = [];
      groups[pageNum].push(cards[i]);
    }
    return groups;
  }

  /**
   * Determine which page the user is currently viewing.
   * Samples one card per page group (O(pages) instead of O(cards)).
   */
  function getCurrentVisiblePage(groups) {
    var viewportMiddle = window.scrollY + window.innerHeight / 2;
    var closestPage = 1;
    var closestDist = Infinity;

    for (var pageNum in groups) {
      var pageCards = groups[pageNum];
      // Sample middle card for best position estimate
      var card = pageCards[Math.floor(pageCards.length / 2)];
      if (!card || card.offsetHeight === 0) continue;

      var rect = card.getBoundingClientRect();
      var cardMiddle = rect.top + window.scrollY + rect.height / 2;
      var dist = Math.abs(cardMiddle - viewportMiddle);

      if (dist < closestDist) {
        closestDist = dist;
        closestPage = parseInt(pageNum, 10);
      }
    }

    return closestPage;
  }

  /**
   * Unload images for an array of card elements.
   * Saves original src/srcset to data attributes, replaces with blank GIF.
   * Also disables scroll on slides containers to release compositing layers.
   */
  function unloadImages(cardElements) {
    for (var i = 0; i < cardElements.length; i++) {
      // Disable scrollable slides to release GPU compositing layers
      var slides = cardElements[i].querySelector('[data-slides]');
      if (slides) slides.style.overflowX = 'hidden';

      var imgs = cardElements[i].querySelectorAll('img');
      for (var j = 0; j < imgs.length; j++) {
        var img = imgs[j];
        // Skip if already unloaded by this script
        if (img.hasAttribute('data-mm-src')) continue;
        // Skip deferred images (not yet loaded, already blank)
        if (img.hasAttribute('data-deferred')) continue;

        // Save original values
        img.setAttribute('data-mm-src', img.getAttribute('src') || '');
        if (img.hasAttribute('srcset')) {
          img.setAttribute('data-mm-srcset', img.getAttribute('srcset'));
          img.removeAttribute('srcset');
        }
        // Replace with blank - browser releases decoded bitmap
        img.setAttribute('src', BLANK_SRC);
      }
    }
  }

  /**
   * Reload images for an array of card elements.
   * Restores original src/srcset from data attributes.
   * Also re-enables scroll on slides containers.
   */
  function reloadImages(cardElements) {
    for (var i = 0; i < cardElements.length; i++) {
      // Restore scrollable slides (CSS rule takes over)
      var slides = cardElements[i].querySelector('[data-slides]');
      if (slides) slides.style.overflowX = '';

      var imgs = cardElements[i].querySelectorAll('img[data-mm-src]');
      for (var j = 0; j < imgs.length; j++) {
        var img = imgs[j];
        // Restore srcset first (before src, so browser can choose correct source)
        if (img.hasAttribute('data-mm-srcset')) {
          img.setAttribute('srcset', img.getAttribute('data-mm-srcset'));
          img.removeAttribute('data-mm-srcset');
        }
        // Restore src
        img.setAttribute('src', img.getAttribute('data-mm-src'));
        img.removeAttribute('data-mm-src');
      }
    }
  }

  /**
   * Main management function. Called on scroll (throttled) and when
   * new pages are added to the grid.
   */
  function managePages() {
    if (!grid || !shouldActivate()) return;

    var groups = getPageGroups();
    var currentPage = getCurrentVisiblePage(groups);
    var pageNums = Object.keys(groups);

    for (var i = 0; i < pageNums.length; i++) {
      var page = parseInt(pageNums[i], 10);
      var distance = Math.abs(page - currentPage);

      if (distance > PAGE_BUFFER) {
        // Far from viewport: unload images
        if (!unloadedPages[page]) {
          unloadImages(groups[page]);
          unloadedPages[page] = true;
        }
      } else {
        // Near viewport: reload images if previously unloaded
        if (unloadedPages[page]) {
          reloadImages(groups[page]);
          delete unloadedPages[page];
        }
      }
    }

    // Clean up stale entries (pages removed by filter changes, etc.)
    for (var p in unloadedPages) {
      if (!groups[p]) {
        delete unloadedPages[p];
      }
    }
  }

  /**
   * Reload ALL pages (used when switching to desktop viewport).
   */
  function reloadAllPages() {
    if (!grid) return;
    var groups = getPageGroups();
    for (var p in unloadedPages) {
      if (groups[p]) {
        reloadImages(groups[p]);
      }
    }
    unloadedPages = {};
  }

  // --- Event Handlers ---

  function onScroll() {
    if (throttleTimer) return;
    throttleTimer = setTimeout(function () {
      throttleTimer = null;
      managePages();
    }, SCROLL_THROTTLE_MS);
  }

  function onResize() {
    if (!shouldActivate() && isActive) {
      // Viewport grew to desktop size: restore all images
      reloadAllPages();
      isActive = false;
    } else if (shouldActivate() && !isActive) {
      // Viewport shrank to mobile: activate management
      isActive = true;
      managePages();
    }
  }

  // --- Initialization ---

  function init() {
    // Find the product grid
    grid = document.querySelector('[ref="grid"]');
    if (!grid) return;

    if (!shouldActivate()) return;
    isActive = true;

    // Listen for scroll
    window.addEventListener('scroll', onScroll, { passive: true });

    // Listen for viewport changes (orientation change, etc.)
    window.addEventListener('resize', onResize, { passive: true });

    // Observe grid for new children added by infinite scroll.
    // Debounced to 1 call per frame (multiple mutations can fire in quick succession).
    var managePending = false;
    var childObserver = new MutationObserver(function () {
      if (!managePending) {
        managePending = true;
        requestAnimationFrame(function () {
          managePending = false;
          managePages();
        });
      }
    });
    childObserver.observe(grid, { childList: true });

    // Initial check (in case page loaded at a deep scroll position)
    managePages();
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();